import * as ToastPrimitive from '@radix-ui/react-toast';
import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import * as React from 'react';

import { MAX_VISIBLE_TOASTS, TOAST_DURATION_MS } from '../../constants/toast.js';

const toastVariants = cva(
  'pointer-events-auto flex w-full items-start justify-between gap-3 rounded-md border border-border bg-background-light p-3 text-sm shadow-md',
  {
    variants: {
      variant: {
        default: 'text-foreground',
        error: 'text-destructive',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

type ToastVariant = NonNullable<VariantProps<typeof toastVariants>['variant']>;

interface ToastMessage {
  id: number;
  message: string;
  variant: ToastVariant;
}

// Radix Toast only renders toasts; this module-level store lets non-React code (mutation
// callbacks) raise one without holding a hook.
let nextToastId = 0;
let toastMessages: ToastMessage[] = [];
const toastListeners = new Set<() => void>();

const setToastMessages = (nextToastMessages: ToastMessage[]) => {
  toastMessages = nextToastMessages;
  toastListeners.forEach((notifyListener) => notifyListener());
};

const subscribeToToasts = (onToastsChange: () => void) => {
  toastListeners.add(onToastsChange);
  return () => {
    toastListeners.delete(onToastsChange);
  };
};

const getToastMessages = () => toastMessages;

const showToast = (message: string, variant: ToastVariant) =>
  setToastMessages([...toastMessages, { id: nextToastId++, message, variant }]);

const dismissToast = (id: number) =>
  setToastMessages(toastMessages.filter((toastMessage) => toastMessage.id !== id));

const toast = Object.assign((message: string) => showToast(message, 'default'), {
  error: (message: string) => showToast(message, 'error'),
});

interface ToasterProps {
  /** Accessible name of each toast's close button; the caller localizes it. */
  closeLabel: string;
}

const Toaster = ({ closeLabel }: ToasterProps) => {
  const messages = React.useSyncExternalStore(subscribeToToasts, getToastMessages);

  return (
    <ToastPrimitive.Provider duration={TOAST_DURATION_MS} swipeDirection="right">
      {/* Queued toasts are not mounted, so their auto-dismiss timer starts only once shown. */}
      {messages.slice(0, MAX_VISIBLE_TOASTS).map(({ id, message, variant }) => (
        <ToastPrimitive.Root
          key={id}
          className={toastVariants({ variant })}
          onOpenChange={(open) => {
            if (!open) dismissToast(id);
          }}
        >
          <ToastPrimitive.Description>{message}</ToastPrimitive.Description>
          <ToastPrimitive.Close className="shrink-0 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring">
            <X className="h-4 w-4" />
            <span className="sr-only">{closeLabel}</span>
          </ToastPrimitive.Close>
        </ToastPrimitive.Root>
      ))}
      <ToastPrimitive.Viewport className="pointer-events-none fixed bottom-0 right-0 z-[10000] flex w-full max-w-sm flex-col gap-2 p-4 outline-none" />
    </ToastPrimitive.Provider>
  );
};

export { toast, Toaster, toastVariants };
